import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import i18n from '../../i18n';

// Load the account preference before saving changes. A failed read must never
// overwrite a preference from another device with this browser's cached value.
export default function LanguagePreferenceSync() {
  const auth = useSelector((state) => state.auth);
  const identity = auth.isAuthenticated && !auth.loading ? auth.user_id : null;
  useEffect(() => {
    if (!identity) return undefined;
    let disposed = false;
    let ready = false;
    let applying = false;
    let changedWhileLoading = false;
    let saving = Promise.resolve();
    let loading = false;
    const pendingKey = `mz-language-pending:${identity}`;
    const pending = () => { try { return localStorage.getItem(pendingKey); } catch { return null; } };
    const markPending = (language) => { try { localStorage.setItem(pendingKey, language); } catch { /* Local cache is optional. */ } };
    const config = { withCredentials: true };
    const report = (state) => {
      if (!disposed) window.dispatchEvent(new CustomEvent('mz-language-sync', { detail: state }));
    };
    const save = (language) => {
      markPending(language);
      // Serialize writes so a slower response cannot restore an older choice.
      saving = saving.then(async () => {
        if (disposed) return;
        report('saving');
        try {
          await axios.put('/api/profile/language', { language }, config);
          try { if (pending() === language) localStorage.removeItem(pendingKey); } catch { /* Storage unavailable. */ }
          report('saved');
        } catch { report('error'); }
      });
    };
    const changed = (language) => {
      if (applying || disposed) return;
      if (!ready) { changedWhileLoading = true; markPending(language); return; }
      save(language);
    };
    i18n.on('languageChanged', changed);
    const load = async () => {
      if (loading || disposed) return;
      loading = true;
      try {
        const { data } = await axios.get('/api/profile/language', config);
        if (disposed) return;
        ready = true;
        const queued = pending();
        if (queued === "de" || queued === "en") {
          applying = true;
          await i18n.changeLanguage(queued);
          applying = false;
          save(queued);
        } else if (changedWhileLoading || !['de', 'en'].includes(data.language)) {
          save(i18n.resolvedLanguage || 'de');
        } else {
          applying = true;
          await i18n.changeLanguage(data.language);
          applying = false;
          report('saved');
        }
      } catch { report('error'); } finally { loading = false; }
    };
    load();
    // Retry a failed initial read when connectivity returns or the user returns.
    const retry = () => { if (!ready) load(); else { const queued = pending(); if (queued === 'de' || queued === 'en') save(queued); } };
    window.addEventListener('online', retry);
    window.addEventListener('focus', retry);
    return () => {
      report('idle');
      disposed = true;
      i18n.off('languageChanged', changed);
      window.removeEventListener('online', retry);
      window.removeEventListener('focus', retry);
    };
  }, [identity]);
  return null;
}
